import { useEffect } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { CERRAR_ORDEN_COMPRA, DELETE_ORDEN_COMPRA, GET_ORDEN_COMPRA } from './gql.js';

export default function OrdenCompraDetail({ ordenId, onBack, onEdit }) {
  const [{ data, fetching, error }, reexecute] = useQuery({ query: GET_ORDEN_COMPRA, variables: { id: ordenId } });
  const [, deleteOrden] = useMutation(DELETE_ORDEN_COMPRA);
  const [, cerrarOrden] = useMutation(CERRAR_ORDEN_COMPRA);

  useEffect(() => {
    reexecute({ requestPolicy: 'network-only' });
  }, [ordenId, reexecute]);

  const orden = data?.getOrdenCompra;

  const handleDelete = async () => {
    if (!ordenId) return;
    const confirmation = window.confirm('¿Eliminar esta orden de compra? Esta acción no se puede deshacer.');
    if (!confirmation) return;
    await deleteOrden({ id: ordenId });
    onBack?.();
  };

  const handleCerrar = async () => {
    if (!ordenId) return;
    await cerrarOrden({ id: ordenId });
    reexecute({ requestPolicy: 'network-only' });
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title={`Orden #${orden?.id ?? ''}`}
        subtitle="Detalle completo de la orden de compra y sus ítems."
        actions={
          <button
            type="button"
            onClick={() => onEdit?.(orden)}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Editar
          </button>
        }
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        {fetching ? (
          <p className="text-sm text-muted">Cargando información…</p>
        ) : error ? (
          <p className="text-sm text-red-300">{error.message}</p>
        ) : orden ? (
          <div className="space-y-6">
            <dl className="grid grid-cols-12 gap-4 text-sm text-[rgb(var(--fg))]">
              <div className="col-span-12 md:col-span-4">
                <dt className="font-medium text-muted">Proveedor</dt>
                <dd className="mt-1">{orden.proveedorNombre || `#${orden.proveedorId}`}</dd>
              </div>
              <div className="col-span-12 md:col-span-4">
                <dt className="font-medium text-muted">Fecha</dt>
                <dd className="mt-1">{orden.fecha ? new Date(orden.fecha).toLocaleDateString() : '—'}</dd>
              </div>
              <div className="col-span-12 md:col-span-4">
                <dt className="font-medium text-muted">Estado</dt>
                <dd className="mt-1">{orden.estado}</dd>
              </div>
              <div className="col-span-12">
                <dt className="font-medium text-muted">Observaciones</dt>
                <dd className="mt-1 whitespace-pre-line">{orden.observacion || '—'}</dd>
              </div>
            </dl>

            <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)]">
              <table className="w-full text-left text-sm">
                <thead className="bg-[rgba(var(--subpanel),0.4)] text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-3">Descripción</th>
                    <th className="px-4 py-3">Cantidad</th>
                    <th className="px-4 py-3">Precio unitario</th>
                    <th className="px-4 py-3">Descuento %</th>
                    <th className="px-4 py-3">Subtotal</th>
                    <th className="px-4 py-3">Recibido</th>
                  </tr>
                </thead>
                <tbody>
                  {orden.items?.length ? (
                    orden.items.map((item) => (
                      <tr key={item.id} className="border-t border-[rgb(var(--border))]/60">
                        <td className="px-4 py-3">{item.descripcion}</td>
                        <td className="px-4 py-3">{Number(item.cantidad).toLocaleString()}</td>
                        <td className="px-4 py-3">{Number(item.precioUnit).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</td>
                        <td className="px-4 py-3">{item.dtoPorc}%</td>
                        <td className="px-4 py-3">{Number(item.subtotal).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</td>
                        <td className="px-4 py-3">{Number(item.cantidadRecibida ?? 0).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                        No hay ítems cargados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end text-sm font-semibold text-[rgb(var(--fg))]">
              Total:{' '}
              {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(orden.total ?? 0)}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted">No encontramos la orden solicitada.</p>
        )}
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Volver
          </button>
        }
        right={
          <div className="flex flex-wrap gap-3">
            {orden?.estado !== 'CERRADA' ? (
              <button
                type="button"
                onClick={handleCerrar}
                className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                Cerrar orden
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onEdit?.(orden)}
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Editar
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-xl bg-red-600/90 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
            >
              Eliminar
            </button>
          </div>
        }
      />
    </div>
  );
}
